import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { createTicketSlice } from "./slices/createTicketSlice";
import { createConfigSlice } from "./slices/createConfigSlice";
import { createPaymentSlice } from "./slices/createPaymentSlice";
import { createPaymentMethodsSlice } from "./slices/createPaymentMethodsSlice";
import { createDiscountsSlice } from "./slices/createDiscountsSlice";
import { createCompanySlice } from "./slices/createCompanySlice";
import { createCardSlice } from "./slices/createCardSlice";
import { createSurchargeSlice } from "./slices/createSurchargeSlice";

const useStore = create(
  immer((set, get) => ({
    ...createTicketSlice(set, get),
    ...createConfigSlice(set, get),
    ...createPaymentSlice(set, get),
    ...createPaymentMethodsSlice(set, get),
    ...createDiscountsSlice(set, get),
    ...createCompanySlice(set, get),
    ...createCardSlice(set, get),
    ...createSurchargeSlice(set, get),
  }))
);

// Ticket Accessors
export const useTicket = () => useStore((state) => state.ticket);
export const setTicketLoading = (...args) =>
  useStore.getState().ticketActions.setLoading(...args);
export const setTicketData = (...args) =>
  useStore.getState().ticketActions.setData(...args);
export const setTicketError = (...args) =>
  useStore.getState().ticketActions.setError(...args);

// Config Accessors
export const useConfig = () => useStore((state) => state.config);
export const setConfig = (...args) =>
  useStore.getState().configActions.setConfig(...args);

// Payment Accessors
export const usePayment = () => useStore((state) => state.payment);
export const setPaymentIsProcessing = (...args) =>
  useStore.getState().paymentActions.setIsProcessing(...args);
export const setPaymentError = (...args) =>
  useStore.getState().paymentActions.setError(...args);
export const setPaymentSuccess = (...args) =>
  useStore.getState().paymentActions.setSuccess(...args);

// Payment Methods Accessors
export const usePaymentMethods = () =>
  useStore((state) => state.paymentMethods);
export const setSavedPaymentMethods = (...args) =>
  useStore.getState().paymentMethodsActions.setSavedPaymentMethods(...args);
export const setShowAddPaymentForm = (...args) =>
  useStore.getState().paymentMethodsActions.setShowAddPaymentForm(...args);
export const setSelectedPaymentMethod = (...args) =>
  useStore.getState().paymentMethodsActions.setSelectedPaymentMethod(...args);
export const setShowSavedPaymentMethods = (...args) =>
  useStore.getState().paymentMethodsActions.setShowSavedPaymentMethods(...args);
export const setPaymentMethodsLoading = (...args) =>
  useStore.getState().paymentMethodsActions.setLoading(...args);
export const setPaymentMethodsError = (...args) =>
  useStore.getState().paymentMethodsActions.setError(...args);
export const setPaymentMethodsSuccess = (...args) =>
  useStore.getState().paymentMethodsActions.setSuccess(...args);
export const setSaveStripePaymentMethodLoading = (...args) =>
  useStore
    .getState()
    .paymentMethodsActions.setSaveStripePaymentMethodLoading(...args);

// Discounts Accessors
export const useDiscounts = () => useStore((state) => state.discounts);
export const setApplyingPromoCode = (...args) =>
  useStore.getState().discountsActions.setApplyingPromoCode(...args);
export const setApplyingGiftCard = (...args) =>
  useStore.getState().discountsActions.setApplyingGiftCard(...args);
export const setPromoCodeSuccess = (...args) =>
  useStore.getState().discountsActions.setPromoCodeSuccess(...args);
export const setGiftCardSuccess = (...args) =>
  useStore.getState().discountsActions.setGiftCardSuccess(...args);
export const setGiftCardError = (...args) =>
  useStore.getState().discountsActions.setGiftCardError(...args);
export const setPromoCodeError = (...args) =>
  useStore.getState().discountsActions.setPromoCodeError(...args);

// Company Accessors
export const useCompany = () => useStore((state) => state.company);
export const setCompanyLoading = (...args) =>
  useStore.getState().companyActions.setLoading(...args);
export const setCompanyData = (...args) =>
  useStore.getState().companyActions.setData(...args);
export const setCompanyError = (...args) =>
  useStore.getState().companyActions.setError(...args);
export const markCompanyAsFetched = (...args) =>
  useStore.getState().companyActions.markCompanyAsFetched(...args);
export const isCompanyFetched = (...args) =>
  useStore.getState().companyActions.isCompanyFetched(...args);

// Card Accessors
export const useCard = () => useStore((state) => state.card);
export const setCardData = (...args) =>
  useStore.getState().cardActions.setCardData(...args);

// Surcharge Accessors
export const useSurcharge = () => useStore((state) => state.surcharge);
export const setSurchargeData = (...args) =>
  useStore.getState().surchargeActions.setSurchargeData(...args);
export const setShowSurchargeConfirmationModal = (...args) =>
  useStore
    .getState()
    .surchargeActions.setShowSurchargeConfirmationModal(...args);
